// R4/R5 (DOM edge) — measure a rendered pane's block layout into the
// `PaneMetrics` the pure `anchor-geometry` module consumes. Kept out of the pure
// module (which must never touch the DOM) and out of the hooks (which stay thin):
// this is the one place that reads element geometry.

import type { DocumentModel } from "@bindery/shared/corpus/document";
import type { BlockBox, PaneMetrics } from "./anchor-geometry";

/**
 * Measure each block's pixel top/height in the scroll container's content
 * space. Blocks are matched to their DOM element by `data-block-id` (retelling
 * paragraphs) or `data-start` (headings and original blocks). Tops are
 * content-relative (independent of the current scroll offset) so segments stay
 * valid as the reader scrolls; they only need rebuilding on resize / font load.
 */
export function measurePane(
  container: HTMLElement,
  model: DocumentModel,
): PaneMetrics {
  const containerTop = container.getBoundingClientRect().top;
  const scrollTop = container.scrollTop;
  const boxes: BlockBox[] = [];

  for (const block of model.blocks) {
    const selector = block.id
      ? `[data-block-id="${block.id}"]`
      : `[data-start="${String(block.start)}"]`;
    const el = container.querySelector<HTMLElement>(selector);
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    boxes.push({
      start: block.start,
      end: block.end,
      id: block.id,
      top: rect.top - containerTop + scrollTop,
      height: rect.height,
    });
  }

  // Content ends at the last block's bottom — not `scrollHeight`, which includes
  // the reading-room bottom padding and would push the end sentinel too far.
  const contentHeight = boxes.reduce(
    (max, b) => Math.max(max, b.top + b.height),
    0,
  );
  return { boxes, contentHeight };
}
